import { rawCategoriasData } from "@/components/v0/banco-images-data";

import {
  ACTIVITY_COVER_CHARACTER_CATALOG,
  getCharacterActivityCoverUrls,
  resolveActivityCoverAssetUrl,
} from "./activity-cover-character-catalog";

export const ACTIVITY_COVER_SOURCE_MODES = ["subject", "characters", "mixed"] as const;

export type ActivityCoverSourceMode = (typeof ACTIVITY_COVER_SOURCE_MODES)[number];

export type ActivityCoverDefaults = {
  readonly sourceMode: ActivityCoverSourceMode;
  readonly categoryId?: string;
};

export type ActivityCoverCatalog = {
  readonly all: readonly string[];
  readonly byCategory: Readonly<Record<string, readonly string[]>>;
  readonly characters: readonly string[];
  readonly genericCharacters: readonly string[];
  readonly charactersByCategory: Readonly<Record<string, readonly string[]>>;
  readonly parentByCategory: Readonly<Record<string, string>>;
};

type RawImageNode = {
  readonly id?: unknown;
  readonly imagens?: unknown;
  readonly subcategorias?: unknown;
};

function unique(values: readonly string[]): string[] {
  return [...new Set(values.filter(Boolean))];
}

export function buildActivityCoverCatalog(
  rawData: unknown = rawCategoriasData,
  origin?: string
): ActivityCoverCatalog {
  const all: string[] = [];
  const byCategory: Record<string, string[]> = {};
  const parentByCategory: Record<string, string> = {};

  const visit = (nodes: unknown, parentCategoryId?: string) => {
    if (!Array.isArray(nodes)) return;
    for (const rawNode of nodes) {
      if (!rawNode || typeof rawNode !== "object") continue;
      const node = rawNode as RawImageNode;
      const id = typeof node.id === "string" ? node.id.trim() : "";
      const images = Array.isArray(node.imagens)
        ? node.imagens.filter((image): image is string => typeof image === "string" && image.length > 0)
        : [];
      if (id) byCategory[id] = unique([...(byCategory[id] ?? []), ...images]);
      if (id && parentCategoryId) parentByCategory[id] = parentCategoryId;
      all.push(...images);
      visit(node.subcategorias, id || parentCategoryId);
    }
  };

  if (rawData && typeof rawData === "object") {
    Object.values(rawData as Record<string, unknown>).forEach((nodes) => visit(nodes));
  }

  const charactersByCategory: Record<string, string[]> = {};
  for (const categoryId of Object.keys(byCategory)) {
    const urls = ACTIVITY_COVER_CHARACTER_CATALOG
      .filter((entry) => entry.categoryIds.includes(categoryId))
      .map((entry) => resolveActivityCoverAssetUrl(entry.path, origin));
    if (urls.length > 0) charactersByCategory[categoryId] = urls;
  }
  const genericCharacters = ACTIVITY_COVER_CHARACTER_CATALOG
    .filter((entry) => entry.categoryIds.length === 0)
    .map((entry) => resolveActivityCoverAssetUrl(entry.path, origin));

  return {
    all: unique(all),
    byCategory,
    characters: getCharacterActivityCoverUrls({ origin }),
    genericCharacters,
    charactersByCategory,
    parentByCategory,
  };
}

export function normalizeActivityCoverDefaults(value: unknown): ActivityCoverDefaults | null {
  if (!value || typeof value !== "object") return null;
  const candidate = value as { sourceMode?: unknown; categoryId?: unknown };
  if (!ACTIVITY_COVER_SOURCE_MODES.includes(candidate.sourceMode as ActivityCoverSourceMode)) return null;

  const categoryId = typeof candidate.categoryId === "string" ? candidate.categoryId.trim() : "";
  return {
    sourceMode: candidate.sourceMode as ActivityCoverSourceMode,
    ...(categoryId ? { categoryId } : {}),
  };
}

export function normalizeActivityCoverDefaultsResponse(response: unknown): ActivityCoverDefaults | null {
  if (!response || typeof response !== "object" || !("activityCoverDefaults" in response)) return null;
  return normalizeActivityCoverDefaults(
    (response as { activityCoverDefaults?: unknown }).activityCoverDefaults
  );
}

export function resolveActivityCoverModalSelections({
  defaults,
  currentImageCategoryId,
  allCategoriesId,
}: {
  readonly defaults: ActivityCoverDefaults | null | undefined;
  readonly currentImageCategoryId?: string;
  readonly allCategoriesId: string;
}) {
  const defaultCategoryId = defaults?.categoryId?.trim() || allCategoriesId;
  return {
    defaultCategoryId,
    browsingCategoryId: currentImageCategoryId || defaultCategoryId,
  };
}

function stableIndex(seed: string, length: number): number {
  let hash = 2166136261;
  for (let index = 0; index < seed.length; index += 1) {
    hash ^= seed.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return (hash >>> 0) % length;
}

export function createActivityCoverSeed(prefix = "activity"): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return `${prefix}:${crypto.randomUUID()}`;
  }
  return `${prefix}:${Date.now()}:${Math.random()}`;
}

export function resolveActivityCover({
  defaults,
  seed,
  existingCover,
  fallbackUrl,
  catalog = buildActivityCoverCatalog(),
}: {
  readonly defaults: ActivityCoverDefaults | null | undefined;
  readonly seed: string;
  readonly existingCover?: string | null;
  readonly fallbackUrl?: string | null;
  readonly catalog?: ActivityCoverCatalog;
}): string {
  const preserved = existingCover?.trim();
  if (preserved) return preserved;
  if (!defaults) return fallbackUrl?.trim() ?? "";

  const categoryId = defaults.categoryId;
  const categoryChain: string[] = [];
  const visited = new Set<string>();
  let currentCategoryId = categoryId;
  while (currentCategoryId && !visited.has(currentCategoryId)) {
    categoryChain.push(currentCategoryId);
    visited.add(currentCategoryId);
    currentCategoryId = catalog.parentByCategory[currentCategoryId];
  }

  const firstNonEmpty = (pools: readonly (readonly string[])[]) =>
    pools.find((pool) => pool.length > 0) ?? [];
  const subjectPools = categoryChain.map((id) => catalog.byCategory[id] ?? []);
  const characterPools = categoryChain.map((id) => catalog.charactersByCategory[id] ?? []);
  const mixedPools = categoryChain.map((id, index) => unique([
    ...(subjectPools[index] ?? []),
    ...(characterPools[index] ?? []),
  ]));

  const preferredPools = defaults.sourceMode === "subject"
    ? subjectPools
    : defaults.sourceMode === "characters"
      ? characterPools
      : mixedPools;
  const pool = firstNonEmpty([...preferredPools, catalog.genericCharacters, catalog.all]);
  if (pool.length === 0) return fallbackUrl?.trim() ?? "";
  return pool[stableIndex(seed, pool.length)] ?? fallbackUrl?.trim() ?? "";
}
